import { i18n } from "@/localization";

// Country-specific base types
const TUNISIA_BASE_TYPES = ["jardinEnfant", "ecole", "college", "lycee"];
const KENYA_BASE_TYPES = ["preSchool", "prePrimary", "middle", "junior", "senior"];

/**
 * Get valid base types based on country code
 * @param {string} countryCode - Country code (TN for Tunisia, KE for Kenya)
 * @returns {Array<string>} Array of valid base types for the country
 */
function getValidBaseTypes(countryCode) {
  if (countryCode === "KE") {
    return KENYA_BASE_TYPES;
  }
  // Default to Tunisia types (TN or any other)
  return TUNISIA_BASE_TYPES;
}

/**
 * Map individual school types to their base type for grouping
 * @param {string} type - Individual school type
 * @param {string} countryCode - Country code (TN for Tunisia, KE for Kenya)
 * @returns {string} Base type for grouping, or null if not a valid base type
 */
function mapTypeToBaseType(type, countryCode = "TN") {
  const validBaseTypes = getValidBaseTypes(countryCode);
  return validBaseTypes.includes(type) ? type : null;
}

/**
 * Normalize school type array to a combined type key
 * Handles all possible combinations of school types
 * @param {Array<string>} type - School type(s) as array
 * @param {string} countryCode - Country code (TN for Tunisia, KE for Kenya)
 * @returns {string} Combined type key for translation lookup
 */
function normalizeSchoolType(type, countryCode = "TN") {
  const validBaseTypes = getValidBaseTypes(countryCode);
  const defaultType = countryCode === "KE" ? "preSchool" : "ecole";
  
  // Handle null/undefined or non-array
  if (!type || !Array.isArray(type)) return defaultType;
  
  const types = type.filter(Boolean); // Remove any null/undefined values
  
  // Handle empty array
  if (types.length === 0) return defaultType;
  
  // Map all types to their base types and filter out invalid types
  const baseTypes = types
    .map(t => mapTypeToBaseType(t, countryCode))
    .filter(Boolean); // Remove null values (invalid types)
  
  // Handle case where no valid base types found
  if (baseTypes.length === 0) return defaultType;
  
  // Get unique base types
  const uniqueBaseTypes = [...new Set(baseTypes)];
  
  // Handle single base type
  if (uniqueBaseTypes.length === 1) {
    return uniqueBaseTypes[0];
  }
  
  // Handle multiple base types - create combination key by concatenating
  // Sort by the order defined in validBaseTypes
  // Capitalize first letter of each type except the first one
  const sortedBaseTypes = uniqueBaseTypes.sort((a, b) => {
    const indexA = validBaseTypes.indexOf(a);
    const indexB = validBaseTypes.indexOf(b);
    return indexA - indexB;
  });
  const combinationKey = sortedBaseTypes
    .map((type, index) => index === 0 ? type : type.charAt(0).toUpperCase() + type.slice(1))
    .join("");
  
  // Return the combination key directly
  return combinationKey;
}

/**
 * Get school type label using i18n translations based on building type
 * Handles all possible combinations of school types
 * @param {Object} schoolDetails - School details object with type and countryCode
 * @param {string} locale - Optional locale code (e.g., "ar-AR", "fr-FR", "en-US", "sw-KE"). If not provided, uses current i18n locale
 * @returns {string} School type label in specified or current language
 */
export function getSchoolTypeLabel(schoolDetails, locale = null) {
  if (!schoolDetails || typeof schoolDetails !== "object") {
    // Fallback to default if schoolDetails is invalid
    const translationKey = "buildingType.ecolePrimairePrivee";
    if (locale) {
      const messages = i18n.messages[locale];
      if (messages) {
        const keys = translationKey.split(".");
        let value = messages;
        for (const k of keys) {
          value = value?.[k];
        }
        if (value) return value;
      }
    }
    // Fallback to current locale if provided locale fails or doesn't exist
    return i18n.t(translationKey);
  }

  // Extract type and countryCode from schoolDetails object
  const type = schoolDetails.type;
  const countryCode = schoolDetails.countryCode || "TN";

  // Normalize type array to combined type key
  const normalizedType = normalizeSchoolType(type, countryCode);

  // Get country-specific type key maps
  const tunisiaTypeKeyMap = {
    jardinEnfant: "buildingType.jardinEnfantsPrive",
    ecole: "buildingType.ecolePrimairePrivee",
    college: "buildingType.collegePrive",
    lycee: "buildingType.lyceePrive",
    // All combinations (sorted by order: jardinEnfant, ecole, college, lycee)
    ecoleCollege: "buildingType.collegeEcolePrives",
    ecoleLycee: "buildingType.ecoleLyceePrives",
    collegeLycee: "buildingType.collegeLyceePrives",
    jardinEnfantEcole: "buildingType.jardinEnfantEcolePrives",
    jardinEnfantCollege: "buildingType.jardinEnfantCollegePrives",
    jardinEnfantLycee: "buildingType.jardinEnfantLyceePrives",
    jardinEnfantEcoleCollege: "buildingType.jardinEnfantEcoleCollegePrives",
    jardinEnfantEcoleLycee: "buildingType.jardinEnfantEcoleLyceePrives",
    jardinEnfantCollegeLycee: "buildingType.jardinEnfantCollegeLyceePrives",
    ecoleCollegeLycee: "buildingType.collegeEcoleLyceePrives",
    jardinEnfantEcoleCollegeLycee:
      "buildingType.jardinEnfantEcoleCollegeLyceePrives",
  };

  const kenyaTypeKeyMap = {
    preSchool: "buildingType.preSchoolPrive",
    prePrimary: "buildingType.prePrimaryPrive",
    middle: "buildingType.middlePrive",
    junior: "buildingType.juniorPrive",
    senior: "buildingType.seniorPrive",
    // All combinations (sorted by order: preSchool, prePrimary, middle, junior, senior)
    preSchoolPrePrimary: "buildingType.preSchoolPrePrimaryPrives",
    preSchoolMiddle: "buildingType.preSchoolMiddlePrives",
    preSchoolJunior: "buildingType.preSchoolJuniorPrives",
    preSchoolSenior: "buildingType.preSchoolSeniorPrives",
    prePrimaryMiddle: "buildingType.prePrimaryMiddlePrives",
    prePrimaryJunior: "buildingType.prePrimaryJuniorPrives",
    prePrimarySenior: "buildingType.prePrimarySeniorPrives",
    middleJunior: "buildingType.middleJuniorPrives",
    middleSenior: "buildingType.middleSeniorPrives",
    juniorSenior: "buildingType.juniorSeniorPrives",
    preSchoolPrePrimaryMiddle: "buildingType.preSchoolPrePrimaryMiddlePrives",
    preSchoolPrePrimaryJunior: "buildingType.preSchoolPrePrimaryJuniorPrives",
    preSchoolPrePrimarySenior: "buildingType.preSchoolPrePrimarySeniorPrives",
    preSchoolMiddleJunior: "buildingType.preSchoolMiddleJuniorPrives",
    preSchoolMiddleSenior: "buildingType.preSchoolMiddleSeniorPrives",
    preSchoolJuniorSenior: "buildingType.preSchoolJuniorSeniorPrives",
    prePrimaryMiddleJunior: "buildingType.prePrimaryMiddleJuniorPrives",
    prePrimaryMiddleSenior: "buildingType.prePrimaryMiddleSeniorPrives",
    prePrimaryJuniorSenior: "buildingType.prePrimaryJuniorSeniorPrives",
    middleJuniorSenior: "buildingType.middleJuniorSeniorPrives",
    preSchoolPrePrimaryMiddleJunior: "buildingType.preSchoolPrePrimaryMiddleJuniorPrives",
    preSchoolPrePrimaryMiddleSenior: "buildingType.preSchoolPrePrimaryMiddleSeniorPrives",
    preSchoolPrePrimaryJuniorSenior: "buildingType.preSchoolPrePrimaryJuniorSeniorPrives",
    preSchoolMiddleJuniorSenior: "buildingType.preSchoolMiddleJuniorSeniorPrives",
    prePrimaryMiddleJuniorSenior: "buildingType.prePrimaryMiddleJuniorSeniorPrives",
    preSchoolPrePrimaryMiddleJuniorSenior: "buildingType.preSchoolPrePrimaryMiddleJuniorSeniorPrives",
  };

  const typeKeyMap = countryCode === "KE" ? kenyaTypeKeyMap : tunisiaTypeKeyMap;

  // Try to get specific translation, fallback to generic or first type
  let translationKey = typeKeyMap[normalizedType];

  if (!translationKey) {
    // If no specific combination found, try to extract first base type from combination
    const validBaseTypes = getValidBaseTypes(countryCode);
    const defaultKey =
      countryCode === "KE"
        ? "buildingType.preSchoolPrive"
        : "buildingType.ecolePrivee";
    const firstType = validBaseTypes.find((t) => normalizedType.startsWith(t));
    if (firstType && typeKeyMap[firstType]) {
      translationKey = typeKeyMap[firstType];
    } else {
      translationKey = defaultKey;
    }
  }

  // If locale is provided, get translation from that locale's messages
  if (locale) {
    const messages = i18n.messages[locale];
    if (messages) {
      const keys = translationKey.split(".");
      let value = messages;
      for (const k of keys) {
        value = value?.[k];
      }
      if (value) return value;
    }
  }
  
  // Use current locale if no locale specified or if provided locale failed
  return i18n.t(translationKey);
}

/**
 * Get full school name with type prefix using i18n translations
 * Automatically selects the appropriate name property based on current or specified locale
 * @param {Object} schoolDetails - School details object with type and name properties
 * @param {string} nameProperty - Optional property name for school name (overrides automatic selection)
 * @param {string} locale - Optional locale code (e.g., "ar-AR", "fr-FR", "en-US", "sw-KE"). If not provided, uses current i18n locale
 * @returns {string} Full school name with type prefix in specified or current language
 */
export function getFullSchoolName(schoolDetails, nameProperty = null, locale = null) {
  if (!schoolDetails) return "";
  
  // Determine which name property to use based on locale
  let schoolName = "";
  if (nameProperty) {
    // If nameProperty is explicitly provided, use it
    schoolName = schoolDetails[nameProperty] || "";
  } else {
    // Auto-select based on locale (use provided locale or current locale)
    const targetLocale = locale || i18n.locale || "fr-FR";
    if (targetLocale.startsWith("ar")) {
      // Arabic locale - use ArabicName
      schoolName = schoolDetails.ArabicName || schoolDetails.name || "";
    } else {
      // Other locales - use legalName or name
      schoolName = schoolDetails.legalName || schoolDetails.name || "";
    }
  }
  
  // Pass schoolDetails object to getSchoolTypeLabel so it can access countryCode
  const typeLabel = getSchoolTypeLabel(schoolDetails, locale);
  return schoolName ? `${typeLabel} ${schoolName}` : typeLabel;
}

